import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import ToolBar from "../common/ToolBar";
import Content from "./Content";
import Pagination from "components/Pagination";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  actionModeSet,
  ClassesFilterTypes,
  classesLoaded,
  currentPageClassList,
  currentPageSet,
  filterSet,
  IClass,
  searchTextSet,
  selectClasses,
  selectClassesActionMode,
  selectClassesPagination,
  selectFilteredClasses,
  selectFilterType,
  totalPagesSet,
  updateItemSet,
} from "features/Classes";
import { useFetchClassesQuery } from "app/services/classes";
import { MasterDataContext } from "contexts/MasterDataContext";
import { useContext, useEffect } from "react";
import ClassDetails from "./ClassDetails";
import { ActionType, IFilterOption } from "features/common/Interfaces";
import TableSkeleton from "../common/Skeletons/Table";
import AddClassesDetails from "./AddClasses";
import { FireBaseContext } from "contexts/FireBase";

const customStyles = {
  root: {
    margin: "20px 0",
    borderRadius: "2px",
    boxShadow: "0 2px 4px 0 rgba(179, 187, 199, 1)",
    background: "rgba(255, 255, 255, 1)",
    padding: "0 10px",
  },
};

function Classes() {
  const { logEvent } = useContext(FireBaseContext);
  const { t } = useTranslation();
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const pagination = useAppSelector((state) => selectClassesPagination(state));
  const filteredClasses = useAppSelector((state) =>
    selectFilteredClasses(state)
  );
  const dispatch = useAppDispatch();
  const allClassesList = useAppSelector((state) => selectClasses(state));
  const filteredClassesList = useAppSelector((state) =>
    selectFilteredClasses(state)
  );
  const currentClassList = useAppSelector((state) =>
    currentPageClassList(state)
  );
  const actionMode = useAppSelector((state) => selectClassesActionMode(state));
  const getPage = (value: number) => dispatch(currentPageSet(value));
  const { data: classesData, isLoading } = useFetchClassesQuery(
    schoolDetails?.schoolId,
    {
      skip: !schoolDetails?.schoolId,
    }
  );

  useEffect(() => {
    logEvent("VisitSchoolOnboardingClassesSection");
    return () => {
      dispatch(searchTextSet(""));
    };
  }, []);

  useEffect(() => {
    const totalPages = filteredClasses?.length
      ? Math.ceil(filteredClasses.length / pagination.pageSize)
      : 0;
    dispatch(totalPagesSet(totalPages));
  }, [filteredClasses]);

  useEffect(() => {
    if (classesData?._statusCode === 200) {
      const list = classesData._entity?.map((item) => {
        const classInfo: IClass = {
          grade: item._grade,
          teacher: item._teacherName,
          id: item._id,
          name: item._name,
          teacherId: item._teacherId,
          gradeId: item._gradeId,
          students: item._noOfStudents,
        };
        return {
          ...classInfo,
        };
      });
      dispatch(classesLoaded(list ?? []));
    }
  }, [classesData]);

  const addAction = () => {
    logEvent("ClickOnAddClass");
    dispatch(actionModeSet(ActionType.add));
  };

  const getFilterType = (type: { currentTarget: IFilterOption }) => {
    dispatch(
      filterSet(
        ClassesFilterTypes[
          type.currentTarget.value as keyof typeof ClassesFilterTypes
        ]
      )
    );
  };
  const getSearchValue = (value: string) => {
    if (pagination.currentPage !== 1) dispatch(currentPageSet(1));
    dispatch(searchTextSet(value));
  };
  const currentFilter = useAppSelector((state) => selectFilterType(state));
  const selectedFilter: IFilterOption = {
    value: currentFilter,
    label: t(currentFilter),
  };

  const filterTypes = Object.keys(ClassesFilterTypes).map((type) => ({
    value: type,
    label: t(type),
  })) as IFilterOption[];

  const closeClassDetailsHandler = () => {
    dispatch(actionModeSet(ActionType.list));
    dispatch(updateItemSet(null));
  };

  return (
    <>
      <ClassDetails
        open={[ActionType.update].includes(actionMode)}
        onClose={closeClassDetailsHandler}
      />
      <AddClassesDetails
        open={actionMode === ActionType.add}
        onClose={() => dispatch(actionModeSet(ActionType.list))}
      />
      <Grid container direction="column" sx={customStyles.root}>
        <Grid item xs={12} sx={{ padding: "15px" }}>
          {t("All classes")}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderTop: "1px solid rgb(217 217 217)",
          }}
        >
          <ToolBar
            addLabel={t("Add Class")}
            addAction={addAction}
            noData={isLoading || !allClassesList?.length}
            filterConfig={{
              getFilterType,
              getSearchValue,
              selectedFilter,
              filterTypes,
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderTop: "1px solid rgb(217 217 217)",
          }}
        >
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <Content
              pageList={currentClassList}
              allList={allClassesList}
              filteredList={filteredClassesList}
            />
          )}
        </Grid>
        <Grid item xs={12} sx={{ padding: "20px 0 40px" }}>
          {currentClassList?.length ? (
            <Pagination
              total={pagination.totalPages}
              defaultPage={pagination.currentPage}
              getPage={getPage}
              spreadStyle
            />
          ) : null}
        </Grid>
      </Grid>
    </>
  );
}

export default Classes;
